import AjaxService from '@services/AjaxService';
import BaseRepository, {SelectOption} from '@repositories/desktop/BaseRepository';
import {resolveImageSourcesToDataUris} from '@js/functions/html';

export default class DigitalMailboxRepository extends BaseRepository {
  protected baseUrl = '/api/desktop/digital-mailbox';

  public constructor(protected readonly ajaxService: AjaxService) {
    super(ajaxService);
  }

  public assignDamageRecord(emailId: number, damageRecordAppraisalId: number): Promise<any> {
    return this.ajaxService.postJson(`${this.baseUrl}/emails/${emailId}/damage-record-assignments`, {
      data: {
        attributes: {
          damageRecordAppraisalId,
        },
      },
    });
  }

  /**
   * Builds the URL for downloading or opening a non-inline email attachment.
   * The `disposition` parameter controls the Content-Disposition header on the server.
   */
  public buildAttachmentUrl(
    emailId: string,
    emailAttachmentId: string,
    disposition: 'inline' | 'attachment'
  ): string {
    return `${this.baseUrl}/emails/${emailId}/attachments/${emailAttachmentId}/download?disposition=${disposition}`;
  }

  public loadAvailableCategories(emailId: number): Promise<Array<SelectOption>> {
    return this.loadSelectOptions(`${this.baseUrl}/emails/${emailId}/categories/options`);
  }

  public loadDamageRecordAssignments(emailId: number): Promise<any> {
    return this.ajaxService.getJson(`${this.baseUrl}/emails/${emailId}/damage-record-assignments`);
  }

  /**
   * Loads all non-inline attachments of the given email as a flat array of attribute objects.
   */
  public loadEmailAttachments(emailId: string): Promise<Array<any>> {
    return this.ajaxService.getJson(`${this.baseUrl}/emails/${emailId}/attachments`)
      .then((response: any) => response.data.map((item: any) => ({...item.attributes, id: item.id})));
  }

  public loadEmailCategories(emailId: number): Promise<Array<SelectOption>> {
    return this.loadSelectOptions(`${this.baseUrl}/emails/${emailId}/categories`);
  }

  public loadEmailDetail(emailId: number): Promise<any> {
    return this.ajaxService.getJson(`${this.baseUrl}/emails/${emailId}/show`);
  }

  public loadMailboxOptions(): Promise<Array<SelectOption>> {
    return this.loadSelectOptions(`${this.baseUrl}/emails/options/mailboxes`);
  }

  public loadUserAssignmentOptions(emailId: number): Promise<Array<SelectOption>> {
    return this.loadSelectOptions(`${this.baseUrl}/emails/${emailId}/options/user-assignment`);
  }

  public removeDamageRecordAssignment(emailId: number, assignmentId: number): Promise<any> {
    return this.ajaxService.sendDelete(`${this.baseUrl}/emails/${emailId}/damage-record-assignments/${assignmentId}`);
  }

  /**
   * Replaces inline-attachment src URLs in `html` with Base64 data URIs — the sandboxed iframe
   * (no allow-same-origin) can't send cookies, so those URLs would otherwise return 401.
   * Route pattern must stay in sync with EmailBodyHtmlSanitizer::buildInlineAttachmentUrl().
   */
  public resolveInlineAttachmentImages(html: string): Promise<string> {
    const inlineAttachmentPattern = /^\/api\/desktop\/digital-mailbox\/emails\/\d+\/attachments\/\d+\/inline$/;

    return resolveImageSourcesToDataUris(html, (url: string) => inlineAttachmentPattern.test(url));
  }

  public setEmailCategories(emailId: number, categoryIds: Array<number>): Promise<any> {
    return this.ajaxService.postJson(
      `${this.baseUrl}/emails/${emailId}/categories`,
      {data: {attributes: {categoryIds}}}
    );
  }

  public setEmailUserAssignment(emailId: number, userId: number | null): Promise<any> {
    return this.ajaxService.postJson(
      `${this.baseUrl}/emails/${emailId}/status`,
      {data: {attributes: {action: 'assignUser', userId}}}
    );
  }

  public updateEmail(emailId: number, attributes: any): Promise<any> {
    return this.ajaxService.patchJson(`${this.baseUrl}/emails/${emailId}/update`, {data: {attributes}});
  }

  public updateEmailStatus(emailId: number, action: string, extraAttributes: Record<string, unknown> = {}): Promise<any> {
    return this.ajaxService.postJson(
      `${this.baseUrl}/emails/${emailId}/status`,
      {data: {attributes: {action, ...extraAttributes}}}
    );
  }
}
